import { Button, Checkbox, Tag, Text } from "@ttc3k/trekker";
import { NavArrowDown, NavArrowRight } from "iconoir-react";
import { NavLink } from "react-router";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { memo, useCallback, useMemo, useState } from "react";
import { Box, Divider, Flex } from "styled-system/jsx";
import { useStorefrontView } from "apps/storefront/StorefrontViewContext";
import { Collapsible } from "shared/components";
import type { EventSessionOrder } from "../useCalendarView";
import {
  deriveAttendanceTargets,
  getArrivedAttendanceKeys,
  getEntryAttendanceLabel,
  getGuestLabel,
  type SetOrderArrival,
} from "./arrivalUtils";
import { MarkArrivedModal } from "./MarkArrivedModal";

type EventSaleOrderCardProps = {
  order: EventSessionOrder;
  currentSessionId: string;
  allowArrivalActions?: boolean;
  onSetArrival: SetOrderArrival;
};

const EventSaleOrderCardComponent = ({
  order,
  currentSessionId,
  allowArrivalActions = true,
  onSetArrival,
}: EventSaleOrderCardProps) => {
  const { t } = useTranslation("storefront", { keyPrefix: "CALENDAR" });
  const { t: tArrival } = useTranslation("storefront", {
    keyPrefix: "CALENDAR.SLIDEOUT.ARRIVAL",
  });
  const { basePath } = useStorefrontView();

  const [selectedAttendanceKeys, setSelectedAttendanceKeys] = useState<
    Set<string>
  >(new Set());
  const [isMarkModalOpen, setIsMarkModalOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const attendanceTargets = useMemo(
    () =>
      deriveAttendanceTargets({
        quantity: order.quantity,
        guests: order.guests,
      }),
    [order.guests, order.quantity],
  );
  const attendanceTargetsByGuest = useMemo(() => {
    const grouped = new Map<number, typeof attendanceTargets>();
    for (const target of attendanceTargets) {
      const guestTargets = grouped.get(target.guestIndex) ?? [];
      guestTargets.push(target);
      grouped.set(target.guestIndex, guestTargets);
    }
    return grouped;
  }, [attendanceTargets]);
  const attendanceTargetsByKey = useMemo(
    () => new Map(attendanceTargets.map(target => [target.key, target])),
    [attendanceTargets],
  );
  const arrivedAttendanceKeys = useMemo(
    () =>
      getArrivedAttendanceKeys(
        {
          quantity: order.quantity,
          guests: order.guests,
          arrivalHistory: order.arrivalHistory,
        },
        currentSessionId,
      ),
    [currentSessionId, order.arrivalHistory, order.guests, order.quantity],
  );

  const history = useMemo(
    () =>
      [...(order.arrivalHistory ?? [])]
        .filter((entry): entry is NonNullable<typeof entry> => !!entry)
        .reverse(),
    [order.arrivalHistory],
  );

  const allSessionIds = useMemo(
    () => (order.sessionIds?.length ? order.sessionIds : [currentSessionId]),
    [currentSessionId, order.sessionIds],
  );

  const toggleAttendanceSelection = useCallback(
    (attendanceKey: string, checked: boolean) => {
      setSelectedAttendanceKeys(prev => {
        const next = new Set(prev);
        if (checked) next.add(attendanceKey);
        else next.delete(attendanceKey);
        return next;
      });
    },
    [],
  );

  const handleUndo = useCallback(
    (attendanceKey: string) => {
      void onSetArrival({
        orderId: order.orderId,
        sessionIds: [currentSessionId],
        attendanceKeys: [attendanceKey],
        arrived: false,
      }).catch(() => undefined);
    },
    [currentSessionId, onSetArrival, order.orderId],
  );

  const handleMarkArrivedClose = useCallback(() => {
    setIsMarkModalOpen(false);
    setSelectedAttendanceKeys(new Set());
  }, []);

  return (
    <Flex
      key={order.orderId}
      flexDir="column"
      gap="200"
      py="200"
      px="200"
      borderRadius="md"
      borderWidth="1px"
      borderColor={order.cancelled ? "bg.error.dark" : "border.light"}
      borderStyle={order.cancelled ? "dashed" : "solid"}
    >
      <Flex alignItems="center" justifyContent="space-between">
        <Flex flexDir="column" gap="25">
          <Text
            visual="bodySemiBold"
            color={order.cancelled ? "text.mid" : "text.dark"}
            textDecoration={order.cancelled ? "line-through" : "none"}
          >
            {order.customerName}
          </Text>
          <Text visual="smallMedium" color="text.mid">
            {order.tierName} × {order.quantity}
          </Text>
          <Text visual="smallMedium" color="text.light">
            {t("SLIDEOUT.ORDER_ID_LABEL", { orderId: order.orderId })}
          </Text>
          <Text visual="smallMedium" color="text.light">
            {t("SLIDEOUT.PURCHASE_DATE_LABEL", {
              date: format(new Date(order.purchaseDate), "MMM d, yyyy"),
            })}
          </Text>
        </Flex>

        <Flex gap="150" alignItems="center">
          {order.cancelled && (
            <Tag visual="destructive">{t("ITEMS.CANCELLED_TAG")}</Tag>
          )}
          <NavLink to={`${basePath}/order/${order.orderId}`}>
            <Button mode="light" Icon={NavArrowRight} size="sm" />
          </NavLink>
        </Flex>
      </Flex>

      {!order.cancelled && order.quantity > 0 && (
        <>
          <Divider borderColor="border.light" />

          <Flex flexDir="column" gap="100">
            {Array.from({ length: order.quantity }, (_, index) => {
              const targets = attendanceTargetsByGuest.get(index) ?? [];
              const hasOptions = targets.some(
                target => target.optionIndex != null,
              );
              return (
                <Flex key={index} flexDir="column" gap="75">
                  {hasOptions && (
                    <Text visual="smallMedium" color="text.dark">
                      {getGuestLabel(order, index, tArrival)}
                    </Text>
                  )}
                  {targets.map(target => {
                    const isArrived =
                      allowArrivalActions &&
                      arrivedAttendanceKeys.has(target.key);
                    const rowLabel =
                      target.optionIndex == null
                        ? getGuestLabel(order, index, tArrival)
                        : target.optionLabel?.trim() ||
                          tArrival("OPTION_FALLBACK", {
                            number: target.optionIndex + 1,
                          });

                    return (
                      <Flex
                        key={target.key}
                        alignItems="center"
                        justifyContent="space-between"
                        pl={hasOptions ? "200" : undefined}
                      >
                        <Flex alignItems="center" gap="150">
                          {!allowArrivalActions ? null : isArrived ? (
                            <Box
                              w="16px"
                              h="16px"
                              borderRadius="full"
                              bg="bg.success.dark"
                              flexShrink={0}
                            />
                          ) : (
                            <Checkbox
                              checked={selectedAttendanceKeys.has(target.key)}
                              onCheckedChange={({ checked }) =>
                                toggleAttendanceSelection(target.key, !!checked)
                              }
                            />
                          )}
                          <Text
                            visual={
                              target.optionIndex == null
                                ? "smallMedium"
                                : "smallRegular"
                            }
                            color={
                              target.optionIndex == null
                                ? "text.dark"
                                : "text.mid"
                            }
                          >
                            {rowLabel}
                          </Text>
                        </Flex>

                        {allowArrivalActions && isArrived && (
                          <Flex alignItems="center" gap="150">
                            <Tag visual="highlight">
                              {tArrival("ARRIVED_TAG")}
                            </Tag>
                            <Button
                              visual="unstyled"
                              color="text.accent.blue.mid"
                              _hover={{ color: "text.accent.blue.dark" }}
                              onClick={() => handleUndo(target.key)}
                            >
                              {tArrival("UNDO")}
                            </Button>
                          </Flex>
                        )}
                      </Flex>
                    );
                  })}
                </Flex>
              );
            })}
          </Flex>

          {allowArrivalActions && selectedAttendanceKeys.size > 0 && (
            <Button
              mode="light"
              alignSelf="flex-start"
              onClick={() => setIsMarkModalOpen(true)}
            >
              {tArrival("MARK_AS_ARRIVED", {
                count: selectedAttendanceKeys.size,
              })}
            </Button>
          )}

          {history.length > 0 && (
            <Box>
              <Button
                visual="unstyled"
                Icon={NavArrowDown}
                iconProps={{
                  style: {
                    transform: isHistoryOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.15s",
                  },
                }}
                color="text.mid"
                onClick={() => setIsHistoryOpen(v => !v)}
              >
                {tArrival("HISTORY_TOGGLE", { count: history.length })}
              </Button>
              <Collapsible open={isHistoryOpen}>
                <Flex flexDir="column" gap="100" mt="150">
                  {history.map((entry, i) => (
                    <Box
                      key={i}
                      borderLeftWidth="2px"
                      borderColor="border.light"
                      pl="150"
                      py="25"
                    >
                      <Text visual="smallSemiBold" color="text.dark">
                        {entry.action === "ARRIVED"
                          ? tArrival("HISTORY_ARRIVED")
                          : tArrival("HISTORY_UNMARKED")}
                        {(entry.sessionIds?.length ?? 0) > 1 &&
                          ` ${tArrival("HISTORY_ALL_DAYS")}`}
                      </Text>
                      <Text visual="smallRegular" color="text.mid">
                        {getEntryAttendanceLabel(
                          order,
                          entry,
                          tArrival,
                          attendanceTargetsByKey,
                        )}
                        {" — "}
                        {format(
                          new Date(entry.timestamp),
                          "MMM d, yyyy h:mm a",
                        )}
                        {entry.performedByName &&
                          ` — ${tArrival("HISTORY_BY", {
                            name: entry.performedByName,
                          })}`}
                      </Text>
                      {entry.note && (
                        <Text
                          visual="smallRegular"
                          color="text.mid"
                          fontStyle="italic"
                        >
                          {entry.note}
                        </Text>
                      )}
                    </Box>
                  ))}
                </Flex>
              </Collapsible>
            </Box>
          )}
        </>
      )}

      {allowArrivalActions && (
        <MarkArrivedModal
          isOpen={isMarkModalOpen}
          onClose={handleMarkArrivedClose}
          orderId={order.orderId}
          selectedAttendanceKeys={Array.from(selectedAttendanceKeys)}
          currentSessionId={currentSessionId}
          allSessionIds={allSessionIds}
          onSetArrival={onSetArrival}
        />
      )}
    </Flex>
  );
};

export const EventSaleOrderCard = memo(EventSaleOrderCardComponent);
